import { useCallback } from "react";
import { QueryKey, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BuyerIdentifiersQuery,
  BuyerIdentifiersQueryVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlClient, useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const BUYER_IDENTIFIERS_QUERY = graphql(`
  query buyerIdentifiers($ids: [Int!], $guids: [String!]) {
    buyers(BuyersRequest: { ids: $ids, guids: $guids }) {
      results {
        id
        guid
        name
      }
    }
  }
`);

export type BuyerIdentifier = BuyerIdentifiersQuery["buyers"]["results"][number];

// Pass either `ids` or `guids`; when both are set the numeric ids win.
type Lookup = BuyerIdentifiersQueryVariables;

function buyerIdentifiersKey(lookup: Lookup): QueryKey {
  return ["buyerIdentifiers", lookup];
}

/**
 * A buyer's numeric id, guid and name. Only the legacy report builder needs this: its saved
 * sections store numeric buyer ids (CpvChartEditor, SuppliersEditor via BuyerMultiselect), and its
 * notice links pass `buyerIds` (useNoticeListsPageState). Delete it with the report builder, or
 * once saved sections and those links use guids.
 */
export function useBuyerIdentifiers(
  lookup: Lookup,
  options?: UseQueryOptions<BuyerIdentifiersQuery, unknown, BuyerIdentifiersQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    buyerIdentifiersKey(lookup),
    BUYER_IDENTIFIERS_QUERY,
    [lookup],
    { ...REACT_QUERY_OPTIONS_NEVER_REFETCH, ...options },
  );

  return { data: data?.buyers.results, ...rest };
}

/** Imperative `useBuyerIdentifiers`, sharing its cache, for lookups made in event handlers. */
export function useFetchBuyerIdentifiers() {
  const queryClient = useQueryClient();
  const { client } = useGraphQlClient();

  return useCallback(
    async (lookup: Lookup): Promise<BuyerIdentifier[]> => {
      const data = await queryClient.fetchQuery({
        queryKey: buyerIdentifiersKey(lookup),
        queryFn: () => client.request(BUYER_IDENTIFIERS_QUERY, lookup),
        ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      });
      return data.buyers.results;
    },
    [queryClient, client],
  );
}
